/**
 * Local Habits Storage (IndexedDB)
 * For personal use without Supabase
 */

import Dexie, { type Table } from 'dexie';

export interface LocalHabit {
  id: string;
  user_id: string;
  title: string;
  value_link?: string;
  cadence: { days: string[] };
  streak: number;
  paused: boolean;
  created_at: string;
  updated_at: string;
}

class LocalHabitsDB extends Dexie {
  habits!: Table<LocalHabit, string>;
  completions!: Table<{ id?: number; habit_id: string; date: string; completed: boolean }, number>;

  constructor() {
    super('LocalHabitsDB');
    this.version(1).stores({
      habits: 'id, user_id, created_at',
      completions: '++id, habit_id, date',
    });
  }
}

const habitsDb = new LocalHabitsDB();
const GUEST_USER_ID = 'guest-user';

export async function getLocalHabits(): Promise<LocalHabit[]> {
  return await habitsDb.habits.where('user_id').equals(GUEST_USER_ID).toArray();
}

export async function createLocalHabit(input: {
  title: string;
  value_link?: string;
  cadence?: { days: string[] };
}): Promise<LocalHabit> {
  const habit: LocalHabit = {
    id: `habit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    user_id: GUEST_USER_ID,
    title: input.title,
    value_link: input.value_link,
    cadence: input.cadence || { days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] },
    streak: 0,
    paused: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  await habitsDb.habits.add(habit);
  return habit;
}

export async function updateLocalHabit(habitId: string, updates: Partial<LocalHabit>): Promise<LocalHabit> {
  const habit = await habitsDb.habits.get(habitId);
  if (!habit) throw new Error('Habit not found');

  const updated = {
    ...habit,
    ...updates,
    updated_at: new Date().toISOString(),
  };

  await habitsDb.habits.update(habitId, updated);
  return updated;
}

export async function deleteLocalHabit(habitId: string): Promise<void> {
  await habitsDb.habits.delete(habitId);
  // Also delete completions
  await habitsDb.completions.where('habit_id').equals(habitId).delete();
}

export async function markHabitComplete(habitId: string, date: string = new Date().toISOString().split('T')[0]): Promise<void> {
  const existing = await habitsDb.completions
    .where('habit_id').equals(habitId)
    .and((c) => c.date === date)
    .first();

  if (!existing) {
    await habitsDb.completions.add({
      habit_id: habitId,
      date,
      completed: true,
    });

    // Update streak
    const habit = await habitsDb.habits.get(habitId);
    if (habit) {
      await updateLocalHabit(habitId, { streak: habit.streak + 1 });
    }
  }
}



